import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { Button } from '~/ui/button'
import { Input } from '~/ui/input'
import { Label } from '~/ui/label'
import { Textarea } from '~/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/ui/select'
import { Switch } from '~/ui/switch'
import { Slider } from '~/ui/slider'
import { Badge } from '~/ui/badge'
import { ArrowLeft, MessageSquare, Save } from 'lucide-react'
import { voiceMessagesPath } from '~/common/paths'
import { useSafeMutation } from '~/common/use-safe-mutation'
import { PreviewAudioPlayer } from './components/preview-audio-player'
import { useCurrentOrganization } from '~/auth/use-current-organization'

const CREATE_VOICE_MESSAGE_MUTATION = gql(`
  mutation CreateVoiceMessage($input: CreateVoiceMessageInput!) {
    createVoiceMessage(input: $input) {
      voiceMessage {
        id
        text
        ttsStatus
        enabled
        ttsModelId
        ttsVoiceId
        ttsSpeed
        ttsVolume
        ttsEmotion
      }
      errors
    }
  }
`)

const CARTESIA_VOICE_OPTIONS_QUERY = gql(`
  query CartesiaVoiceOptions {
    cartesiaVoiceOptions {
      defaultVoiceId
      defaultModelId
      defaultSpeed
      defaultVolume
      speedMin
      speedMax
      volumeMin
      volumeMax
      emotions
      recommendedVoiceIds
      voices {
        id
        voiceId
        name
        language
        description
        emotive
        recommended
        tags
      }
    }
  }
`)

const PREVIEW_CARTESIA_VOICE_MUTATION = gql(`
  mutation PreviewCartesiaVoice($input: PreviewCartesiaVoiceInput!) {
    previewCartesiaVoice(input: $input) {
      audioBase64
      contentType
      errors
    }
  }
`)

const DEFAULT_EMOTION = 'none'
const EMOTION_NONE_LABEL = 'None (no emotion control)'

interface FormData {
  title: string
  text: string
  enabled: boolean
  ttsVoiceId: string
  ttsSpeed: number
  ttsVolume: number
  ttsEmotion: string
  ttsModelId: string
}

export const NewVoiceMessageScreen = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState<FormData>({
    title: '',
    text: '',
    enabled: true,
    ttsVoiceId: '',
    ttsSpeed: 1,
    ttsVolume: 1,
    ttsEmotion: DEFAULT_EMOTION,
    ttsModelId: 'sonic-3',
  })
  const [errors, setErrors] = useState<string[]>([])
  const [, setPreviewError] = useState<string | null>(null)
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null)
  const previewAudioUrlRef = useRef<string | null>(null)

  const { id: organizationId } = useCurrentOrganization()
  const [createVoiceMessage, { loading: saving }] = useSafeMutation(CREATE_VOICE_MESSAGE_MUTATION)
  const { data: cartesiaOptionsData, loading: cartesiaOptionsLoading } = useQuery(
    CARTESIA_VOICE_OPTIONS_QUERY
  )
  const [previewCartesiaVoice, { loading: previewing }] = useSafeMutation(
    PREVIEW_CARTESIA_VOICE_MUTATION
  )

  const cartesiaOptions = cartesiaOptionsData?.cartesiaVoiceOptions

  const defaultEmotion = DEFAULT_EMOTION

  // Only set defaults on initial load when cartesiaOptions first becomes available
  const hasInitializedDefaults = useRef(false)
  useEffect(() => {
    if (!cartesiaOptions || hasInitializedDefaults.current) return

    setFormData((prev) => {
      // Only set defaults if fields are empty/unset (initial state)
      const updates: Partial<FormData> = {}

      if (!prev.ttsVoiceId) {
        updates.ttsVoiceId = cartesiaOptions.defaultVoiceId
      }
      if (!prev.ttsModelId) {
        updates.ttsModelId = cartesiaOptions.defaultModelId
      }
      if (prev.ttsSpeed === 1) {
        // Only reset speed if it's still at the initial default (1)
        updates.ttsSpeed = cartesiaOptions.defaultSpeed
      }
      if (prev.ttsVolume === 1) {
        // Only reset volume if it's still at the initial default (1)
        updates.ttsVolume = cartesiaOptions.defaultVolume
      }
      if (!prev.ttsEmotion || prev.ttsEmotion.length === 0) {
        updates.ttsEmotion = defaultEmotion
      }

      // Mark as initialized after first run
      hasInitializedDefaults.current = true

      // Only update if there are changes
      if (Object.keys(updates).length === 0) {
        return prev
      }

      return { ...prev, ...updates }
    })
  }, [cartesiaOptions, defaultEmotion])

  // Clean up blob URL on unmount only
  useEffect(() => {
    return () => {
      if (previewAudioUrlRef.current) {
        URL.revokeObjectURL(previewAudioUrlRef.current)
        previewAudioUrlRef.current = null
      }
    }
  }, [])

  const availableVoices = useMemo(() => {
    const allVoices = cartesiaOptions?.voices ?? []
    // Filter to only emotive voices when an emotion is selected
    if (formData.ttsEmotion && formData.ttsEmotion !== 'none') {
      return allVoices.filter((voice: any) => voice.emotive === true)
    }
    return allVoices
  }, [cartesiaOptions?.voices, formData.ttsEmotion])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors([])

    // Client-side validation
    const newErrors: string[] = []
    if (!organizationId) newErrors.push('Organization is required')
    if (!formData.text.trim()) newErrors.push('Message text is required')
    if (formData.text.length > 5000) newErrors.push('Message text must be 5000 characters or less')

    if (newErrors.length > 0) {
      setErrors(newErrors)
      return
    }

    const result = await createVoiceMessage({
      variables: {
        input: {
          input: {
            organizationId,
            title: formData.title.trim(),
            text: formData.text.trim(),
            enabled: formData.enabled,
            ttsVoiceId: formData.ttsVoiceId,
            ttsSpeed: formData.ttsSpeed,
            ttsVolume: formData.ttsVolume,
            ttsEmotion: formData.ttsEmotion,
            ttsModelId: formData.ttsModelId,
          },
        },
      },
    })

    if (result.data?.createVoiceMessage?.voiceMessage) {
      navigate(voiceMessagesPath({}))
    } else if (result.data?.createVoiceMessage?.errors) {
      setErrors(result.data.createVoiceMessage.errors)
    }
  }

  const handleInputChange = (field: keyof FormData) => (value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([])
    }
  }

  const handleSliderChange = (field: 'ttsSpeed' | 'ttsVolume') => (value: number[]) => {
    const numericValue = value[0]
    handleInputChange(field)(numericValue)
  }

  const handlePreview = async () => {
    setPreviewError(null)

    if (!formData.text.trim()) {
      setPreviewError('Enter message text to preview the voice')
      return
    }

    // Revoke previous URL if it exists
    if (previewAudioUrlRef.current) {
      URL.revokeObjectURL(previewAudioUrlRef.current)
      previewAudioUrlRef.current = null
    }
    setPreviewAudioUrl(null)

    try {
      // Build input object, only including voice_id if it's set
      const previewInput: any = {
        text: formData.text.trim(),
        ttsSpeed: formData.ttsSpeed,
        ttsVolume: formData.ttsVolume,
        ttsEmotion: formData.ttsEmotion === 'none' ? null : formData.ttsEmotion,
        ttsModelId: formData.ttsModelId,
      }

      // Only include ttsVoiceId if it's actually set (not empty string)
      if (formData.ttsVoiceId && formData.ttsVoiceId.trim() !== '') {
        previewInput.ttsVoiceId = formData.ttsVoiceId
      }

      const result = await previewCartesiaVoice({
        variables: {
          input: {
            input: previewInput,
          },
        },
      })

      const previewResponse = result.data?.previewCartesiaVoice

      if (!previewResponse) {
        console.error('No preview response received')
        setPreviewError('Failed to generate preview - no response from server')
        return
      }

      if (previewResponse.errors && previewResponse.errors.length > 0) {
        console.error('Preview errors:', previewResponse.errors)
        setPreviewError(previewResponse.errors[0])
        return
      }

      const audioBase64 = previewResponse.audioBase64
      if (!audioBase64) {
        console.error('No audio data in preview response')
        setPreviewError('No audio data received from server')
        return
      }

      try {
        const byteCharacters = atob(audioBase64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i += 1) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], {
          type: previewResponse.contentType ?? 'audio/mpeg',
        })

        if (blob.size === 0) {
          console.error('Created blob has zero size')
          setPreviewError('Generated audio file is empty')
          return
        }

        const url = URL.createObjectURL(blob)
        console.log('Preview audio created:', { size: blob.size, type: blob.type, url })
        previewAudioUrlRef.current = url
        setPreviewAudioUrl(url)
      } catch (decodeError) {
        console.error('Error decoding base64 audio:', decodeError)
        setPreviewError('Failed to decode audio data')
      }
    } catch (error) {
      console.error('Preview generation error:', error)
      setPreviewError('Failed to generate preview - please try again')
    }
  }

  return (
    <div className="flex h-screen flex-col">
      <div className="flex-shrink-0 border-b p-6">
        <div className="mx-auto max-w-2xl">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate(voiceMessagesPath({}))}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Voice Messages
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-2xl">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <MessageSquare className="h-6 w-6" />
                <div>
                  <CardTitle>Create New Voice Message</CardTitle>
                  <CardDescription>
                    Create a text-to-speech announcement that can be scheduled for playback
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {errors.length > 0 && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-4">
                    <div className="text-sm text-red-700">
                      <div className="mb-2 font-medium">Please fix the following errors:</div>
                      <ul className="list-inside list-disc space-y-1">
                        {errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title')(e.target.value)}
                    placeholder="Give your voice message a name..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="text">Message Text *</Label>
                  <Textarea
                    id="text"
                    value={formData.text}
                    onChange={(e) => handleInputChange('text')(e.target.value)}
                    placeholder="Enter the text that will be converted to speech..."
                    rows={6}
                    className="resize-none"
                  />
                  <div className="text-muted-foreground flex justify-between text-sm">
                    <span>The text that will be converted to speech using AI</span>
                    <span className={formData.text.length > 5000 ? 'text-red-600' : ''}>
                      {formData.text.length}/5000 characters
                    </span>
                  </div>
                </div>

                <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-medium">Cartesia Voice Settings</div>
                      <p className="text-muted-foreground text-sm">
                        Choose the Sonic 3 voice, speaking style, and preview the result before
                        saving.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handlePreview}
                      disabled={previewing || cartesiaOptionsLoading || !formData.ttsVoiceId}
                    >
                      {previewing ? 'Generating preview...' : 'Preview voice'}
                    </Button>
                  </div>

                  <div className='grid gap-4 md:grid-cols-1'>
                    <div className="space-y-2">
                      <Label>Emotion</Label>
                      <Select
                        value={formData.ttsEmotion}
                        onValueChange={handleInputChange('ttsEmotion')}
                        disabled={cartesiaOptionsLoading}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select emotion" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem key="none" value="none">
                            {EMOTION_NONE_LABEL}
                          </SelectItem>
                          {(cartesiaOptions?.emotions ?? []).map((emotion) => (
                            <SelectItem key={emotion} value={emotion}>
                              {emotion.charAt(0).toUpperCase() + emotion.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground text-xs">
                        Choose an emotional tone or select "None" for neutral delivery
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-1">
                    <div className="space-y-2">
                      <Label htmlFor="voice">Voice</Label>
                      <Select
                        value={formData.ttsVoiceId}
                        onValueChange={handleInputChange('ttsVoiceId')}
                        disabled={cartesiaOptionsLoading || availableVoices.length === 0}
                      >
                        <SelectTrigger size="lg">
                          <SelectValue
                            placeholder={
                              cartesiaOptionsLoading ? 'Loading voices...' : 'Select a voice'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className="max-h-[400px]">
                          {availableVoices.map((voice: any) => {
                            const voiceId = voice.voiceId ?? voice.id
                            const gender = voice.gender
                              ? voice.gender.charAt(0).toUpperCase() + voice.gender.slice(1)
                              : ''
                            const emotiveTags = voice.tags
                              ? voice.tags
                                  .split(', ')
                                  .filter((t: string) => t === 'emotive' || t === 'entertainment')
                              : []

                            return (
                              <SelectItem key={voiceId} value={voiceId}>
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {voice.name ?? 'Unnamed voice'}
                                    </span>
                                    {gender && (
                                      <span className="text-muted-foreground text-xs">•</span>
                                    )}
                                    {gender && (
                                      <span className="text-muted-foreground text-xs">
                                        {gender}
                                      </span>
                                    )}
                                    {emotiveTags.map((tag: string) => (
                                      <Badge
                                        key={tag}
                                        variant="secondary"
                                        className="text-[9px] tracking-wide uppercase"
                                      >
                                        {tag}
                                      </Badge>
                                    ))}
                                  </div>
                                  {voice.description && (
                                    <span className="text-muted-foreground text-xs">
                                      {voice.description}
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            )
                          })}
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground text-xs">
                        Powered by Cartesia Sonic 3 • {availableVoices.length} voices available
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Speed ({formData.ttsSpeed.toFixed(2)}×)</Label>
                      <Slider
                        value={[formData.ttsSpeed]}
                        min={cartesiaOptions?.speedMin ?? 0.6}
                        max={cartesiaOptions?.speedMax ?? 1.5}
                        step={0.05}
                        onValueChange={handleSliderChange('ttsSpeed')}
                      />
                      <p className="text-muted-foreground text-xs">
                        Slow it down for clarity or speed it up for urgency
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Volume ({formData.ttsVolume.toFixed(2)}×)</Label>
                      <Slider
                        value={[formData.ttsVolume]}
                        min={cartesiaOptions?.volumeMin ?? 0}
                        max={cartesiaOptions?.volumeMax ?? 2}
                        step={0.05}
                        onValueChange={handleSliderChange('ttsVolume')}
                      />
                      <p className="text-muted-foreground text-xs">
                        Boost or soften the announcement volume relative to the default
                      </p>
                    </div>
                  </div>

                  {previewAudioUrl && (
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <PreviewAudioPlayer
                        audioUrl={previewAudioUrl}
                        onFinished={() => {
                          if (previewAudioUrlRef.current) {
                            URL.revokeObjectURL(previewAudioUrlRef.current)
                            previewAudioUrlRef.current = null
                          }
                          setPreviewAudioUrl(null)
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <Switch
                      id="enabled"
                      checked={formData.enabled}
                      onCheckedChange={handleInputChange('enabled')}
                    />
                    <Label htmlFor="enabled" className="text-sm font-medium">
                      Enable voice message
                    </Label>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    Disabled voice messages will not be played even if scheduled
                  </p>
                </div>

                <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
                  <div className="text-sm text-blue-700">
                    <div className="mb-2 font-medium">What happens next?</div>
                    <ul className="list-inside list-disc space-y-1">
                      <li>
                        The text will be converted to speech using AI (this may take a few moments)
                      </li>
                      <li>You can create schedules to control when this message plays</li>
                      <li>Messages can be set to play at specific times or regular intervals</li>
                      <li>
                        You can choose whether messages interrupt current music or wait for tracks
                        to finish
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 border-t pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(voiceMessagesPath({}))}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    <Save className="mr-2 h-4 w-4" />
                    {saving ? 'Creating...' : 'Create Voice Message'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
